import React from 'react';
import {
  IconButton,
  Tooltip,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Divider,
  Typography,
  Chip,
} from '@mui/material';
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Animation as AnimationIcon,
  ViewCompact as CompactIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { 
  toggleTheme, 
  setPrimaryColor, 
  toggleAnimations, 
  toggleCompactMode 
} from '../../services/slices/themeSlice';

const ThemeToggle: React.FC = () => {
  const dispatch = useAppDispatch();
  const { mode, primaryColor, animations, compactMode } = useAppSelector((state) => state.theme);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  const handleColorChange = (color: string) => {
    dispatch(setPrimaryColor(color));
  };

  const handleAnimationsToggle = () => {
    dispatch(toggleAnimations());
  };

  const handleCompactModeToggle = () => {
    dispatch(toggleCompactMode());
  };

  const colorOptions = [
    { name: 'Blue', value: '#1976d2' },
    { name: 'Purple', value: '#7b1fa2' },
    { name: 'Green', value: '#388e3c' },
    { name: 'Orange', value: '#f57c00' },
    { name: 'Red', value: '#d32f2f' },
    { name: 'Teal', value: '#00796b' },
  ];

  return (
    <>
      <Tooltip title="Theme Settings">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{
            ml: 1,
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 280,
            mt: 1.5,
            borderRadius: 2,
            '& .MuiMenuItem-root': {
              borderRadius: 1,
              mx: 1,
              my: 0.5,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Theme Mode Toggle */}
        <MenuItem onClick={handleThemeToggle}>
          <ListItemIcon>
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </ListItemIcon>
          <ListItemText>
            Switch to {mode === 'dark' ? 'Light' : 'Dark'} Mode
          </ListItemText>
          <Chip 
            label={mode === 'dark' ? 'Dark' : 'Light'} 
            size="small" 
            variant="outlined"
          />
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        {/* Color Palette */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaletteIcon fontSize="small" />
            Primary Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {colorOptions.map((color) => (
              <Tooltip key={color.value} title={color.name}>
                <IconButton
                  size="small"
                  onClick={() => handleColorChange(color.value)}
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: color.value,
                    border: primaryColor === color.value ? '3px solid' : '1px solid',
                    borderColor: primaryColor === color.value ? 'text.primary' : 'divider',
                    '&:hover': {
                      backgroundColor: color.value,
                      opacity: 0.8,
                    },
                  }}
                >
                  {primaryColor === color.value && (
                    <CircleIcon sx={{ fontSize: 12, color: 'white' }} />
                  )}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Animation Toggle */}
        <MenuItem onClick={(e) => e.stopPropagation()}>
          <ListItemIcon>
            <AnimationIcon />
          </ListItemIcon>
          <ListItemText>Animations</ListItemText>
          <FormControlLabel
            control={
              <Switch
                checked={animations}
                onChange={handleAnimationsToggle}
                size="small"
              />
            }
            label=""
            sx={{ m: 0 }}
          />
        </MenuItem>

        {/* Compact Mode Toggle */}
        <MenuItem onClick={(e) => e.stopPropagation()}>
          <ListItemIcon>
            <CompactIcon />
          </ListItemIcon>
          <ListItemText>Compact Mode</ListItemText>
          <FormControlLabel
            control={
              <Switch
                checked={compactMode}
                onChange={handleCompactModeToggle}
                size="small"
              />
            }
            label=""
            sx={{ m: 0 }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default ThemeToggle;
